import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { COLOR_SYSTEM, Text, Button, Icon, Spinner, Badge } from 'components/atoms';
import Tabs from 'components/molecules/Tabs';
import { useExperiment, useExperimentProgress, useExperimentEvaluations } from 'hooks/aiReviewTest/useExperiments';
import { TestStatus, TestConfiguration } from 'types/aiReviewTest';
import { PROMPT_TYPE_KOREAN_NAMES } from 'common/models/types';
import { connectEvaluationStream, disconnectEvaluationStream, TestProgress } from 'api/aiReviewEvaluationService';
import { useQueryClient } from 'react-query';
import { ConfirmManager, ToastManager } from 'components/organisms/CommonUI';
import { RootState, AppDispatch } from 'store';
import { runReevaluation } from 'store/slices/reevaluationSlice';

const formatDateTime = (date: string | Date) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

interface ExperimentDetailModalProps {
  experimentId: number;
  onClose: () => void;
  onStop?: () => void;
  onResume?: () => void;
  onRestart?: () => void;
  onDuplicate?: (iterations: number) => void;
}

const ExperimentDetailModal: React.FC<ExperimentDetailModalProps> = ({
  experimentId,
  onStop,
  onResume,
  onRestart,
  onDuplicate,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showProgressMenu, setShowProgressMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // 상세결과 선택 재평가 상태
  const dispatch = useDispatch<AppDispatch>();
  const reeval = useSelector((s: RootState) => s.reevaluation);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const isThisRunning = reeval.running && reeval.experimentId === experimentId;

  const { data: experimentData, isLoading, refetch: refetchExperiment } = useExperiment(experimentId);
  const { data: progressData } = useExperimentProgress(
    experimentId,
    experimentData?.data?.data?.status === TestStatus.RUNNING || experimentData?.data?.data?.status === TestStatus.FAILED
  );
  const { data: evaluationsData, isLoading: isLoadingEvaluations } = useExperimentEvaluations(experimentId);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowProgressMenu(false);
      }
    };

    if (showProgressMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProgressMenu]);

  // 실험이 바뀌면 선택 초기화 (스테일 선택 방지)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [experimentId]);

  // 실행 중인 실험에 대한 스트림 연결
  useEffect(() => {
    if (experimentData?.data?.data?.status === TestStatus.RUNNING) {
      let failedCount = 0;
      
      connectEvaluationStream(
        experimentId,
        // onProgress
        (progress: TestProgress) => {
          // 실패 카운트 누적
          if (progress.status === 'failed') {
            failedCount++;
          }
          
          // React Query 캐시 업데이트 - TestProgress 형식에 맞게 변환
          queryClient.setQueryData(['experiments', experimentId, 'progress'], {
            data: { 
              data: {
                total: progress.totalItems,
                completed: progress.currentItemIndex,
                failed: failedCount,
                currentTask: progress.currentItem,
                errorCount: failedCount,
                message: progress.message
              }
            }
          });
        },
        // onStatus
        (status) => {
          // 상태가 변경되면 실험 정보 다시 불러오기
          if (status !== TestStatus.RUNNING) {
            refetchExperiment();
          }
        },
        // onComplete
        () => {
          // 완료되면 실험 정보 다시 불러오기
          refetchExperiment();
        },
        // onError
        (error) => {
          console.error('Stream error for experiment', experimentId, error);
        }
      );

      // 클린업: 컴포넌트 언마운트 시 스트림 연결 해제
      return () => {
        disconnectEvaluationStream(experimentId);
      };
    }
  }, [experimentId, experimentData?.data?.data?.status, queryClient, refetchExperiment]);

  const experiment = experimentData?.data?.data;
  const progress = progressData?.data?.data;

  if (isLoading || !experiment) {
    return (
      <LoadingContainer>
        <Spinner size="Large" />
      </LoadingContainer>
    );
  }

  const getStatusBadge = (status: TestStatus) => {
    const statusConfig = {
      [TestStatus.PENDING]: { label: '대기중', color: 'Gray' as const },
      [TestStatus.RUNNING]: { label: '실행중', color: 'Primary' as const },
      [TestStatus.COMPLETED]: { label: '완료', color: 'Primary' as const },
      [TestStatus.FAILED]: { label: '실패', color: 'Red' as const },
    };

    const config = statusConfig[status];
    return <Badge content={config.label} color={config.color} />;
  };

  const calculateProgress = () => {
    if (!progress) return 0;
    const total = (progress as any).total || 0;
    const completed = (progress as any).completed || 0;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  const tabs = [
    { id: 'overview', label: '개요' },
    { id: 'configurations', label: '테스트 구성' },
    { id: 'results', label: '상세결과' },
    { id: 'logs', label: '로그' },
  ];

  const handleStopExperiment = () => {
    setShowProgressMenu(false);
    if (onStop) {
      onStop();
    }
  };

  const handleResumeExperiment = () => {
    setShowProgressMenu(false);
    if (onResume) {
      onResume();
    }
  };

  const handleRestartExperiment = () => {
    setShowProgressMenu(false);
    if (onRestart) {
      onRestart();
    }
  };

  const handleDuplicateExperiment = () => {
    setShowProgressMenu(false);
    let iterations = 1;

    ConfirmManager.show({
      title: "추가 실행",
      body: React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "12px" } },
        React.createElement("p", { style: { margin: 0, fontSize: "14px", color: "#374151" } },
          "같은 설정으로 새 실험을 생성합니다."
        ),
        React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "8px" } },
          React.createElement("label", { style: { fontSize: "14px", color: "#6b7280" } }, "반복 횟수:"),
          React.createElement("input", {
            type: "number",
            min: 1,
            defaultValue: 1,
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              iterations = parseInt(e.target.value, 10) || 1;
            },
            style: {
              width: "80px",
              padding: "6px 10px",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              fontSize: "14px",
            },
          })
        )
      ),
      buttons: [
        React.createElement("button", {
          key: "cancel",
          onClick: () => ConfirmManager.hide(),
          style: {
            padding: "8px 16px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            background: "white",
            fontSize: "14px",
            cursor: "pointer",
          },
        }, "취소"),
        React.createElement("button", {
          key: "confirm",
          onClick: () => {
            ConfirmManager.hide();
            if (iterations >= 1 && onDuplicate) {
              onDuplicate(iterations);
            }
          },
          style: {
            padding: "8px 16px",
            border: "none",
            borderRadius: "6px",
            background: "#3b82f6",
            color: "white",
            fontSize: "14px",
            cursor: "pointer",
          },
        }, "실행"),
      ],
    });
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = (evaluations: any[]) => {
    setSelectedIds((prev) =>
      prev.size === evaluations.length ? new Set() : new Set(evaluations.map((e) => e.id))
    );
  };

  const handleSelectedReevaluate = () => {
    setShowProgressMenu(false);

    // 진행 중이면 새로 시작하지 않고 안내 토스트 (전역 기준: 다른 실험 재평가 중이어도 차단)
    if (reeval.running) {
      ToastManager.show({
        content: '현재 재평가가 진행 중입니다. 완료 후 다시 시도해주세요.',
        duration: 3000,
      });
      return;
    }
    if (selectedIds.size === 0) return;

    const ids = Array.from(selectedIds);
    ConfirmManager.show({
      title: '선택 재평가',
      body: `${ids.length}건을 하나씩 재평가합니다. 시간이 오래 걸릴 수 있습니다. 진행하시겠습니까?`,
      buttons: [
        <Button
          key="cancel"
          type="Text"
          size="Medium"
          variant="Outlined"
          color="Secondary"
          onClick={() => ConfirmManager.hide()}
        >
          취소
        </Button>,
        <Button
          key="confirm"
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={() => {
            ConfirmManager.hide();
            setSelectedIds(new Set());
            dispatch(runReevaluation({ experimentId, ids }));
          }}
        >
          재평가
        </Button>,
      ],
    });
  };

  const renderOverviewTab = () => (
    <TabContent>
      <Section>
        <SectionTitle>기본 정보</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <InfoLabel>실험 ID</InfoLabel>
            <InfoValue>#{experiment.id}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>실험명</InfoLabel>
            <InfoValue>{experiment.name}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>상태</InfoLabel>
            <InfoValue>{getStatusBadge(experiment.status)}</InfoValue>
          </InfoItem>
          <InfoItem>
            <InfoLabel>생성일시</InfoLabel>
            <InfoValue>{experiment.createdAt ? formatDateTime(experiment.createdAt) : '-'}</InfoValue>
          </InfoItem>
          {experiment.startedAt && (
            <InfoItem>
              <InfoLabel>시작일시</InfoLabel>
              <InfoValue>{formatDateTime(experiment.startedAt)}</InfoValue>
            </InfoItem>
          )}
          {experiment.completedAt && (
            <InfoItem>
              <InfoLabel>완료일시</InfoLabel>
              <InfoValue>{formatDateTime(experiment.completedAt)}</InfoValue>
            </InfoItem>
          )}
        </InfoGrid>
        {experiment.description && (
          <DescriptionBox>
            <InfoLabel>설명</InfoLabel>
            <Description>{experiment.description}</Description>
          </DescriptionBox>
        )}
      </Section>

      {(experiment.status === TestStatus.RUNNING || experiment.status === TestStatus.FAILED) && (
        <Section>
          <SectionHeader>
            <SectionTitle>
              {experiment.status === TestStatus.RUNNING ? '진행 상황' : '실험 상태'}
            </SectionTitle>
          </SectionHeader>
          {experiment.status === TestStatus.RUNNING && progress ? (
            <ProgressBox>
              <ProgressHeader>
                <ProgressText>
                  {(progress as any).completed} / {(progress as any).total} 완료
                </ProgressText>
                <ProgressPercent>{calculateProgress()}%</ProgressPercent>
              </ProgressHeader>
              <ProgressBar>
                <ProgressFill style={{ width: `${calculateProgress()}%` }} />
              </ProgressBar>
              {(progress as any).currentTask && (
                <CurrentTask>
                  <TaskLabel>현재 작업:</TaskLabel>
                  <TaskText>{(progress as any).currentTask}</TaskText>
                </CurrentTask>
              )}
            </ProgressBox>
          ) : experiment.status === TestStatus.FAILED ? (
            <FailedBox>
              <Icon name="Alert Circle" size={24} color={COLOR_SYSTEM.get("Red")?.[600]} />
              <FailedText>실험이 실패했습니다. 위의 메뉴에서 재개 또는 재시작할 수 있습니다.</FailedText>
            </FailedBox>
          ) : null}
        </Section>
      )}

      {experiment.status === TestStatus.COMPLETED && (
        <Section>
          <SectionTitle>최종 결과</SectionTitle>
          {evaluationsData?.data?.data ? (
            <>
              <ResultsGrid>
                <ResultCard $type="total">
                  <ResultLabel>전체 평가</ResultLabel>
                  <ResultValue>{evaluationsData.data.data.length || 0}</ResultValue>
                </ResultCard>
                <ResultCard $type="success">
                  <ResultLabel>평가 완료</ResultLabel>
                  <ResultValue>
                    {evaluationsData.data.data.filter((e: any) => e.score !== null && e.score !== undefined).length || 0}
                  </ResultValue>
                </ResultCard>
                <ResultCard $type="failed">
                  <ResultLabel>평가 실패</ResultLabel>
                  <ResultValue>
                    {evaluationsData.data.data.filter((e: any) => e.score === null || e.score === undefined).length || 0}
                  </ResultValue>
                </ResultCard>
                <ResultCard $type="rate">
                  <ResultLabel>평균 점수</ResultLabel>
                  <ResultValue>
                    {(() => {
                      const scoredEvaluations = evaluationsData.data.data.filter((e: any) => e.score !== null && e.score !== undefined);
                      if (scoredEvaluations.length === 0) return '-';
                      // score는 DECIMAL 컬럼이라 문자열("85.00")로 전달됨 → Number()로 변환 후 합산(문자열 연결 방지)
                      const avgScore = scoredEvaluations.reduce((sum: number, e: any) => sum + Number(e.score), 0) / scoredEvaluations.length;
                      return `${Math.round(avgScore)}점`;
                    })()}
                  </ResultValue>
                </ResultCard>
              </ResultsGrid>
              {(experiment as any).results && (
                <InfoGrid style={{ marginTop: '16px' }}>
                  <InfoItem>
                    <InfoLabel>실행 결과 (레거시)</InfoLabel>
                    <InfoValue>
                      전체: {(experiment as any).results.total || 0}, 
                      성공: {(experiment as any).results.success || 0}, 
                      실패: {(experiment as any).results.failed || 0}
                    </InfoValue>
                  </InfoItem>
                </InfoGrid>
              )}
            </>
          ) : (
            <EmptyResultsBox>
              <Icon name="Activity" size={24} color={COLOR_SYSTEM.get("Gray")?.[400]} />
              <EmptyResultsText>평가 결과를 불러오고 있습니다...</EmptyResultsText>
            </EmptyResultsBox>
          )}
        </Section>
      )}
    </TabContent>
  );

  const renderConfigurationsTab = () => (
    <TabContent>
      {experiment.configuration?.map((config: TestConfiguration, index: number) => (
        <ConfigCard key={index}>
          <ConfigHeader>
            <ConfigTitle>테스트 구성 #{index + 1}</ConfigTitle>
            <ConfigBadges>
              {config.patientIds?.length > 0 && (
                <Badge content={`환자 ${config.patientIds.length}명`} color="Primary" />
              )}
              {config.aiReviewIds?.length > 0 && (
                <Badge content={`AI Review ${config.aiReviewIds.length}개`} color="Primary" />
              )}
              {config.aiReviewEvaluationIds?.length > 0 && (
                <Badge content={`평가 ${config.aiReviewEvaluationIds.length}개`} color="Gray" />
              )}
            </ConfigBadges>
          </ConfigHeader>
          
          <ConfigContent>
            <ConfigSection>
              <ConfigSectionTitle>AI Review 설정</ConfigSectionTitle>
              <ConfigGrid>
                <ConfigItem>
                  <ConfigLabel>프롬프트 타입</ConfigLabel>
                  <ConfigValue>
                    {config.aiReviewRequest.promptType && 
                      PROMPT_TYPE_KOREAN_NAMES[config.aiReviewRequest.promptType]}
                  </ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>모델</ConfigLabel>
                  <ConfigValue>{config.aiReviewRequest.modelType}</ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>프롬프트 ID</ConfigLabel>
                  <ConfigValue>{config.aiReviewRequest.promptId}</ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>반복 횟수</ConfigLabel>
                  <ConfigValue>{config.iterations}회</ConfigValue>
                </ConfigItem>
              </ConfigGrid>
            </ConfigSection>

            <ConfigSection>
              <ConfigSectionTitle>평가 설정</ConfigSectionTitle>
              <ConfigGrid>
                <ConfigItem>
                  <ConfigLabel>평가 프롬프트 ID</ConfigLabel>
                  <ConfigValue>{config.aiReviewRequest.evaluationPromptId}</ConfigValue>
                </ConfigItem>
                <ConfigItem>
                  <ConfigLabel>평가 모델</ConfigLabel>
                  <ConfigValue>{config.aiReviewRequest.evaluationModel}</ConfigValue>
                </ConfigItem>
                {config.aiReviewRequest.evaluationUseGTSnapshot && (
                  <ConfigItem>
                    <ConfigLabel>GT Snapshot</ConfigLabel>
                    <ConfigValue>사용</ConfigValue>
                  </ConfigItem>
                )}
              </ConfigGrid>
            </ConfigSection>
          </ConfigContent>
        </ConfigCard>
      ))}
    </TabContent>
  );

  const renderResultsTab = () => {
    const evaluations = Array.isArray(evaluationsData?.data?.data) ? evaluationsData.data.data : [];

    if (isLoadingEvaluations) {
      return (
        <TabContent>
          <LoadingContainer>
            <Spinner size="Large" />
          </LoadingContainer>
        </TabContent>
      );
    }

    if (!evaluationsData?.data?.data || evaluations.length === 0) {
      return (
        <TabContent>
          <ComingSoon>
            <Icon name="File Text" size={48} color={COLOR_SYSTEM.get("Gray")?.[400]} />
            <ComingSoonText>아직 생성된 평가 결과가 없습니다.</ComingSoonText>
          </ComingSoon>
        </TabContent>
      );
    }

    const allSelected = evaluations.length > 0 && selectedIds.size === evaluations.length;

    return (
      <TabContent>
        <ResultsTable>
          <thead>
            <tr>
              <CheckboxHeader>
                <input type="checkbox" checked={allSelected} onChange={() => toggleAll(evaluations)} />
              </CheckboxHeader>
              <ResultsTableHeader>평가 ID</ResultsTableHeader>
              <ResultsTableHeader>AI Review ID</ResultsTableHeader>
              <ResultsTableHeader>환자번호</ResultsTableHeader>
              <ResultsTableHeader>평가 점수</ResultsTableHeader>
              <ResultsTableHeader>평가 그룹 ID</ResultsTableHeader>
              <ResultsTableHeader>평가 시점</ResultsTableHeader>
              <ResultsTableHeader>생성일시</ResultsTableHeader>
              <ResultsTableHeader>작업</ResultsTableHeader>
            </tr>
          </thead>
          <tbody>
            {evaluations.map((evaluation: any) => (
              <tr key={evaluation.id}>
                <CheckboxCell>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(evaluation.id)}
                    onChange={() => toggleOne(evaluation.id)}
                  />
                </CheckboxCell>
                <ResultsTableCell>{evaluation.id}</ResultsTableCell>
                <ResultsTableCell>{evaluation.aiReviewId || '-'}</ResultsTableCell>
                <ResultsTableCell>{evaluation.patientId || '-'}</ResultsTableCell>
                <ResultsTableCell>
                  {evaluation.score !== null && evaluation.score !== undefined 
                    ? `${Math.round(evaluation.score)}점` 
                    : '-'}
                </ResultsTableCell>
                <ResultsTableCell>{evaluation.evaluationGroupId || '-'}</ResultsTableCell>
                <ResultsTableCell>
                  {evaluation.evaluatedAt ? formatDateTime(evaluation.evaluatedAt) : '-'}
                </ResultsTableCell>
                <ResultsTableCell>
                  {evaluation.createdAt ? formatDateTime(evaluation.createdAt) : '-'}
                </ResultsTableCell>
                <ResultsTableCell>
                  <ActionButton onClick={() => {
                    // 화면 크기의 90%를 차지하도록 설정, 최대 1920x1080
                    const width = Math.min(1920, window.screen.width * 0.9);
                    const height = Math.min(1080, window.screen.height * 0.9);
                    const left = (window.screen.width - width) / 2;
                    const top = (window.screen.height - height) / 2;

                    window.open(
                      `/admin/ai-reviews/${evaluation.aiReviewId}/popup/evaluation/${evaluation.id}`,
                      `ai-review-${evaluation.aiReviewId}-evaluation-${evaluation.id}`,
                      `width=${width},height=${height},left=${left},top=${top},` +
                        `toolbar=no,location=no,directories=no,status=no,menubar=no,` +
                        `scrollbars=yes,resizable=yes`
                    );
                  }}>
                    <Icon name="External Link" size={16} />
                    상세보기
                  </ActionButton>
                </ResultsTableCell>
              </tr>
            ))}
          </tbody>
        </ResultsTable>
      </TabContent>
    );
  };

  const renderLogsTab = () => (
    <TabContent>
      <ComingSoon>
        <Icon name="Terminal" size={48} color={COLOR_SYSTEM.get("Gray")?.[400]} />
        <ComingSoonText>로그 기능은 추가 개발 예정입니다.</ComingSoonText>
      </ComingSoon>
    </TabContent>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'configurations':
        return renderConfigurationsTab();
      case 'results':
        return renderResultsTab();
      case 'logs':
        return renderLogsTab();
      default:
        return null;
    }
  };

  return (
    <SlideContent>
      <SlideHeader>
        <SlideTitle>실험 #{experiment.id} 상세 정보</SlideTitle>
      </SlideHeader>
      <TabsWrapper>
        <TabsContainer>
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
          <MenuContainer ref={menuRef}>
            <MoreButton onClick={() => setShowProgressMenu(!showProgressMenu)}>
              <Icon name="Dots Horizontal" size={20} />
              {showProgressMenu && (
                <DropdownMenu>
                  {experiment.status === TestStatus.RUNNING && (
                    <DropdownMenuItem onClick={handleStopExperiment}>
                      <Icon name="Pause" size={16} />
                      정지
                    </DropdownMenuItem>
                  )}
                  {(experiment.status === TestStatus.RUNNING || experiment.status === TestStatus.FAILED || 
                    experiment.status === TestStatus.PENDING) && (
                    <DropdownMenuItem onClick={handleResumeExperiment}>
                      <Icon name="Play" size={16} />
                      재개
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleRestartExperiment}>
                    <Icon name="Refresh" size={16} />
                    재시작
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDuplicateExperiment}>
                    <Icon name="Copy" size={16} />
                    추가 실행
                  </DropdownMenuItem>
                  {/* 상세결과 탭에서만 노출. 진행 중엔 dim($dimmed)만 적용하고 클릭은 살려 "진행 중" 토스트를 띄움.
                      비진행 + 선택 0 일 때만 실제 disabled */}
                  {activeTab === 'results' && (
                    <DropdownMenuItem
                      onClick={handleSelectedReevaluate}
                      disabled={!reeval.running && selectedIds.size === 0}
                      $dimmed={reeval.running}
                    >
                      <Icon name="Refresh" size={16} />
                      선택 재평가{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                    </DropdownMenuItem>
                  )}
                </DropdownMenu>
              )}
            </MoreButton>
          </MenuContainer>
        </TabsContainer>
      </TabsWrapper>
      <TabContentWrapper>
        {renderTabContent()}
        {isThisRunning && (
          <ReevalOverlay>
            <Spinner size="Large" />
            <ReevalOverlayText>재평가 중 ({reeval.current}/{reeval.total})…</ReevalOverlayText>
            <ReevalOverlaySub>이 창을 닫아도 재평가는 계속 진행됩니다.</ReevalOverlaySub>
          </ReevalOverlay>
        )}
      </TabContentWrapper>
    </SlideContent>
  );
};

// Styled Components
const SlideContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const SlideHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const SlideTitle = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const TabsWrapper = styled.div`
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
`;

const MenuContainer = styled.div`
  position: relative;
`;

const TabContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  position: relative;
`;

const TabContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const InfoValue = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 500;
`;

const DescriptionBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 8px;
`;

const Description = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  line-height: 1.5;
`;

const ProgressBox = styled.div`
  padding: 20px;
  background: ${COLOR_SYSTEM.get("Blue")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 8px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ProgressText = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const ProgressPercent = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Blue")?.[600]};
`;

const MoreButton = styled.button`
  position: relative;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 140px;
  overflow: hidden;
`;

const DropdownMenuItem = styled.button<{ $dimmed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  text-align: left;
  transition: background-color 0.2s;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.45 : 1)};

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
  transition: width 0.3s ease;
`;

const CurrentTask = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
`;

const TaskLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const TaskText = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
`;

const ResultCard = styled.div<{ $type: string }>`
  padding: 20px;
  background: ${({ $type }) => {
    switch ($type) {
      case 'success': return COLOR_SYSTEM.get("Green")?.[50];
      case 'failed': return COLOR_SYSTEM.get("Red")?.[50];
      case 'rate': return COLOR_SYSTEM.get("Blue")?.[50];
      default: return COLOR_SYSTEM.get("Gray")?.[50];
    }
  }};
  border: 1px solid ${({ $type }) => {
    switch ($type) {
      case 'success': return COLOR_SYSTEM.get("Green")?.[200];
      case 'failed': return COLOR_SYSTEM.get("Red")?.[200];
      case 'rate': return COLOR_SYSTEM.get("Blue")?.[200];
      default: return COLOR_SYSTEM.get("Gray")?.[200];
    }
  }};
  border-radius: 8px;
  text-align: center;
`;

const ResultLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  margin-bottom: 8px;
  display: block;
`;

const ResultValue = styled(Text)`
  font-size: 24px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  display: block;
`;

const ConfigCard = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  overflow: hidden;
`;

const ConfigHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
`;

const ConfigTitle = styled(Text)`
  font-size: 15px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const ConfigBadges = styled.div`
  display: flex;
  gap: 8px;
`;

const ConfigContent = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConfigSectionTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const ConfigItem = styled.div`
  display: flex;
  gap: 8px;
`;

const ConfigLabel = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
  min-width: 120px;
`;

const ConfigValue = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  font-weight: 500;
`;

const ComingSoon = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  gap: 16px;
`;

const ComingSoonText = styled(Text)`
  font-size: 16px;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
`;

const FailedBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: ${COLOR_SYSTEM.get("Red")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Red")?.[200]};
  border-radius: 8px;
`;

const FailedText = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Red")?.[800]};
`;

const ReevalOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.85);
  z-index: 10;
`;

const ReevalOverlayText = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
`;

const ReevalOverlaySub = styled(Text)`
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  overflow: hidden;
`;

const CheckboxHeader = styled.th`
  padding: 12px 16px;
  width: 40px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  text-align: center;
`;

const CheckboxCell = styled.td`
  padding: 12px 16px;
  width: 40px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  text-align: center;
`;

const ResultsTableHeader = styled.th`
  padding: 12px 16px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
`;

const ResultsTableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  
  &:last-child {
    text-align: center;
  }
`;


const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  border-radius: 6px;
  font-size: 13px;
  color: ${COLOR_SYSTEM.get("Gray")?.[700]};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[50]};
    border-color: ${COLOR_SYSTEM.get("Gray")?.[400]};
  }
`;

const EmptyResultsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px;
  background: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
`;

const EmptyResultsText = styled(Text)`
  font-size: 14px;
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

export default ExperimentDetailModal;